import styled from 'styled-components';

export const EditorContainer = styled.div`
  position: relative;
  border: 1px solid #ced4da;
  border-radius: 0;
  background-color: white;
  overflow: auto;
  height: 100%;
`;

export const BlockContainer = styled.div`
  margin: 8px 0;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #f8f9fa;
  overflow: hidden;
  
  .mermaid-container {
    padding: 16px;
    text-align: center;
  }
  
  .excalidraw-container {
    padding: 16px;
    text-align: center;
  }
`;

// Enhanced ListContainer with more robust nested list styling
export const ListContainer = styled.div`
  /* Base list styles */
  .md-list {
    margin: 0.5em 0;
    padding-left: 2.5em; /* Base padding for all lists */
    list-style-position: outside;
    position: relative;
  }

  /* --- Essential Nested List Structure --- */
  /* Force all nested lists to be properly indented - highest priority */
  && .md-ul .md-ul,
  && .md-ol .md-ol,
  && .md-ul .md-ol,
  && .md-ol .md-ul {
    margin-left: 1.5em !important;
    text-indent: 0 !important;
  }
  
  /* Directly target ReactMarkdown nested list structure */
  && ul ul, 
  && ol ol,
  && ul ol,
  && ol ul {
    margin-left: 1.5em !important;
    text-indent: 0 !important;
  }
  
  /* Target nested list items directly */
  && li li {
    padding-left: 2em !important;
    position: relative !important;
  }
  
  /* Force indentation for items that are nested based on markdown column position */
  .md-list-item.is-nested,
  && li[data-is-nested="true"] {
    padding-left: 2em !important;
    margin-left: 2em !important;
  }
  
  /* Basic list item styling */
  .md-list-item {
    margin: 0.3em 0;
    line-height: 1.6;
    position: relative;
    display: list-item; /* Ensure list markers show */
  }
  
  /* --- Tree View Styling --- */
  
  /* Vertical lines for all list items */
  .md-list-item::before,
  && li::before {
    content: "";
    position: absolute;
    top: 0;
    left: -1.5em;
    width: 2px;
    background-color: magenta;
    bottom: 0;
    pointer-events: none; /* Ensure it doesn't interfere with clicking */
  }
  
  /* Last item in a list gets shorter vertical line */
  .md-list-item:last-child::before,
  && li:last-child::before {
    height: 0.9em;
    bottom: auto;
  }
  
  /* Horizontal connector lines for items that have nested lists */
  .md-list-item.has-nested-list::after,
  && li[data-has-nested-list="true"]::after {
    content: "";
    position: absolute;
    top: 1.4em; /* Position just below the text */
    left: -1.5em; /* Start at the vertical line */
    width: 2em; /* Extend towards child items */
    height: 2px;
    background-color: cyan;
    pointer-events: none;
  }
  
  /* Additional selectors and styles for ordered vs unordered lists */
  .md-ul, && ul {
    list-style-type: disc !important;
  }
  
  .md-ul .md-ul, && ul ul {
    list-style-type: circle !important;
  }
  
  .md-ul .md-ul .md-ul, && ul ul ul {
    list-style-type: square !important;
  }
  
  .md-ol, && ol {
    list-style-type: decimal !important;
  }
  
  .md-ol .md-ol, && ol ol {
    list-style-type: lower-alpha !important;
  }
  
  .md-ol .md-ol .md-ol, && ol ol ol {
    list-style-type: lower-roman !important;
  }
  
  /* Force proper display for list items */
  && li {
    display: list-item !important;
  }
  
  /* Fix paragraph spacing in list items */
  .md-list-item > .md-paragraph,
  && li > p {
    margin: 0 !important;
    padding: 0 !important;
    display: inline !important;
  }
  
  /* Ensure links are styled properly */
  a {
    color: #007bff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  /* Basic markdown styling for inside view mode */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 0.5em; /* Add some space */
    margin-bottom: 0.2em;
    font-weight: 600; /* Ensure headings are bold */
    line-height: 1.2; /* Adjust line height for headings */
  }

  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.17em; }
  h4 { font-size: 1em; }
  h5 { font-size: 0.83em; }
  h6 { font-size: 0.67em; }

  p {
    margin: 0 0 0.5em 0; /* Add bottom margin to paragraphs */
  }
  
  strong, b {
    font-weight: bold; /* Ensure bold text is bold */
  }

  em, i {
    font-style: italic; /* Ensure italic text is italic */
  }
  
  blockquote {
    margin: 0.5em 0;
    padding-left: 0.75em;
    border-left: 3px solid #ced4da;
    color: #6c757d;
  }
  
  code {
    background-color: #f8f9fa;
    padding: 0.1em 0.2em;
    border-radius: 3px;
    font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  }
  
  pre {
    background-color: #f8f9fa;
    padding: 0.5em;
    border-radius: 3px;
    overflow-x: auto;
    margin: 0.5em 0;
    font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
    white-space: pre-wrap; /* Ensure code blocks wrap */
  }
  
  img {
    max-width: 100%;
    margin: 0.5em 0;
  }
`;

// Table styling component
export const MarkdownTable = styled.div`
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.5em 0;
    font-size: 0.9em;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    text-align: left;
    border: 1px solid #dee2e6;
    padding: 8px 12px;
    border-bottom: 2px solid #ced4da; /* More prominent bottom border */
    font-size: 0.95em; /* Slightly larger font for header */
  }
  
  td {
    border: 1px solid #dee2e6;
    padding: 6px 12px;
  }
  
  tr:nth-child(even) {
    background-color: #f8f9fa;
  }
  
  tr:hover {
    background-color: #f1f3f5;
  }
`;

// Mermaid diagram styling
export const MermaidContainer = styled.div`
  .mermaid-diagram-container {
    margin: 16px 0;
    display: flex;
    justify-content: center;
    background-color: #f8f9fa;
    border-radius: 6px;
    padding: 16px;
    overflow-x: auto;
  }

  .mermaid-rendered {
    text-align: center;
    
    svg {
      max-width: 100%;
      height: auto;
    }
  }
`;